import { memo } from "react";
import { Card, Typography } from "antd";
import { motion } from "motion/react";
import { StreamingText } from "./StreamingText";
import { MediaRender } from "./MediaRender";
import { AnimatedMediaRender } from "./AnimatedMediaRender";
import { ANIMATION } from "./animationConfig";
import { useImageSlideAnimation } from "./useSlideAnimation";
import type { SlideComponentProps } from "./SlideRenderer";
import type { ImageElement } from "./types";

export const ParagraphImageSlide = memo(
  ({ slide, isActive = false, onComplete }: SlideComponentProps) => {
    const placeholders = slide.placeholders as {
      title: string;
      text: string;
      image_element: ImageElement;
    };
    const title = placeholders.title;
    const text = placeholders.text;
    const imageElement = placeholders.image_element;

    const { imageAsset, isLoading, imageUrl, nextStep, isStepActive } =
      useImageSlideAnimation(imageElement, 3, isActive, onComplete);

    // Not active yet - render static content
    if (!isActive) {
      return (
        <Card>
          <Typography.Title level={4}>{title}</Typography.Title>
          <Typography.Paragraph style={{ marginBottom: 16 }}>
            {text}
          </Typography.Paragraph>
          <MediaRender
            imageElement={imageElement}
            imageUrl={imageUrl}
            imageAsset={imageAsset}
            isLoading={isLoading}
          />
        </Card>
      );
    }

    return (
      <Card>
        {/* Step 0: Title */}
        {isStepActive(0) && (
          <Typography.Title level={4}>
            <StreamingText
              text={title}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Title>
        )}

        {/* Step 1: Text */}
        {isStepActive(1) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <Typography.Paragraph style={{ marginBottom: 16 }}>
              <StreamingText
                text={text}
                animate
                wordDelay={ANIMATION.WORD_DELAY}
                onComplete={nextStep}
              />
            </Typography.Paragraph>
          </motion.div>
        )}

        {/* Step 2: Image */}
        {isStepActive(2) && (
          <AnimatedMediaRender
            imageElement={imageElement}
            imageUrl={imageUrl}
            imageAsset={imageAsset}
            isLoading={isLoading}
            onAnimationComplete={nextStep}
          />
        )}
      </Card>
    );
  }
);

ParagraphImageSlide.displayName = "ParagraphImageSlide";
